import { useContext, useRef, useCallback, useMemo } from 'react';
import { createHMRProofContext } from '../utils/createHMRProofContext';
import { YonduOAuthInteraction } from '@ver.id/libs.ui.yondu';
import {
  getDelivery,
  type DeliveryContext,
  type DeliveryMode,
} from '../journey/delivery';
import { type RonanLifecycleMessage } from './useParentMessaging';

// -- Public types (pages see only this) --

export type JourneyActions = {
  /** Complete the flow — resolve OAuth consent and redirect to (or signal) the client */
  complete: () => Promise<void>;
  /**
   * Cancel the flow from within Ronan.
   * Aborts the active Chulak session (if any) so the wallet backend knows,
   * then rejects the OAuth interaction and redirects to the client.
   *
   * In webhook/embedded mode, settles through the backend instead of navigating
   * and posts `ronan:cancel` to the parent.
   */
  cancel: () => Promise<void>;
  /**
   * Reject the OAuth interaction and redirect to the client.
   *
   * Unlike cancel(), this does NOT touch Chulak — it only ends the OAuth journey.
   * Use for:
   *  - Error/abort screens where the user clicks "Leave"
   *  - Protocol-level events that already ended Chulak (e.g. wallet app cancelled)
   *  - Pre-Chulak error states where no session exists yet
   *
   * In webhook/embedded mode, settles through the backend instead of navigating
   * and posts `ronan:error` to the parent.
   */
  reject: () => Promise<void>;
};

// -- Internal types (ChulakSession uses these) --

type ChulakHandle = {
  abort: () => void;
  cleanup: () => void;
};

export type JourneyManager = JourneyActions & {
  setOAuthInteraction: (interaction: YonduOAuthInteraction) => void;
  registerChulak: (handle: ChulakHandle) => void;
  unregisterChulak: () => void;
  /**
   * Register a handler that displays a generic error page. Called when the
   * journey cannot redirect back to the client (no live OAuth interaction, or
   * the server session expired / a network failure occurred while settling).
   */
  setErrorHandler: (handler: () => void) => void;
  /** Register the parent notifier embedded Ronan reports the flow's end with. */
  setNotifyParent: (fn: (message: RonanLifecycleMessage) => void) => void;
  /** `webhook` in embedded mode, else `redirect`; fixed for the journey. */
  getDeliveryMode: () => DeliveryMode;
  /** True after complete/cancel/reject has been called — guards cleanup on unmount */
  isRedirecting: () => boolean;
};

// -- Context --

export const JourneyContext = createHMRProofContext<JourneyManager | null>(
  'JourneyContext',
  null,
);

// -- Hooks --

/**
 * Create the journey manager. Called once at the App level.
 * Returns a stable (useMemo'd) object safe to pass as context value.
 */
export function useJourneyManager(
  deliveryMode: DeliveryMode = 'redirect',
): JourneyManager {
  const oauthRef = useRef<YonduOAuthInteraction | null>(null);
  const chulakRef = useRef<ChulakHandle | null>(null);
  const redirectingRef = useRef(false);
  const errorHandlerRef = useRef<(() => void) | null>(null);
  const notifyParentRef = useRef<
    ((message: RonanLifecycleMessage) => void) | null
  >(null);

  const setOAuthInteraction = useCallback(
    (interaction: YonduOAuthInteraction) => {
      oauthRef.current = interaction;
    },
    [],
  );

  const registerChulak = useCallback((handle: ChulakHandle) => {
    chulakRef.current = handle;
  }, []);

  const unregisterChulak = useCallback(() => {
    chulakRef.current = null;
  }, []);

  const isRedirecting = useCallback(() => redirectingRef.current, []);

  const setErrorHandler = useCallback((handler: () => void) => {
    errorHandlerRef.current = handler;
  }, []);

  const setNotifyParent = useCallback(
    (fn: (message: RonanLifecycleMessage) => void) => {
      notifyParentRef.current = fn;
    },
    [],
  );

  const getDeliveryMode = useCallback(() => deliveryMode, [deliveryMode]);

  /** Give up on redirecting back to the client and show a generic error page instead. */
  const showGenericError = useCallback(() => {
    redirectingRef.current = false;
    errorHandlerRef.current?.();
  }, []);

  /**
   * Bridges the refs into the shape the delivery strategies expect. Stable —
   * it only reads/writes refs, so it never needs to be rebuilt.
   */
  const deliveryContext = useMemo<DeliveryContext>(
    () => ({
      interaction: {
        get: () => oauthRef.current,
        set: (next) => {
          oauthRef.current = next;
        },
      },
      notifyParent: (message) => notifyParentRef.current?.(message),
    }),
    [],
  );

  const complete = useCallback(async () => {
    if (redirectingRef.current) return;
    if (!oauthRef.current) throw new Error('No OAuth interaction available');
    redirectingRef.current = true;
    try {
      const outcome = await getDelivery(deliveryMode).complete(deliveryContext);
      if (outcome === 'aborted') redirectingRef.current = false;
    } catch (e) {
      redirectingRef.current = false;
      throw e;
    }
  }, [deliveryContext, deliveryMode]);

  /** End the OAuth journey with a reject, falling back to a generic error page. */
  const rejectOAuth = useCallback(
    async (intent: 'cancel' | 'error') => {
      const outcome = await getDelivery(deliveryMode).reject(
        deliveryContext,
        intent,
      );
      if (outcome === 'aborted') showGenericError();
    },
    [deliveryContext, deliveryMode, showGenericError],
  );

  const cancel = useCallback(async () => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    // Tell the wallet backend the user cancelled (best-effort)
    try {
      chulakRef.current?.abort();
    } catch {
      /* ignore */
    }
    await rejectOAuth('cancel');
  }, [rejectOAuth]);

  const reject = useCallback(async () => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    await rejectOAuth('error');
  }, [rejectOAuth]);

  // Stable object: the delivery mode never changes during a journey
  return useMemo(
    () => ({
      setOAuthInteraction,
      registerChulak,
      unregisterChulak,
      setErrorHandler,
      setNotifyParent,
      getDeliveryMode,
      isRedirecting,
      complete,
      cancel,
      reject,
    }),
    [
      setOAuthInteraction,
      registerChulak,
      unregisterChulak,
      setErrorHandler,
      setNotifyParent,
      getDeliveryMode,
      isRedirecting,
      complete,
      cancel,
      reject,
    ],
  );
}

/**
 * Get journey actions for use in pages and components.
 *
 * Returns the context typed as JourneyActions — lifecycle methods
 * are hidden from consumers at the TypeScript level.
 */
export function useJourney(): JourneyActions {
  const ctx = useContext(JourneyContext);
  if (!ctx)
    throw new Error('useJourney must be used within a JourneyContext.Provider');
  return ctx;
}
